import { cn } from '../lib/cn';

export type SegmentedOption<T extends string | number> = {
  value: T;
  /** Short label — these sit side by side, so keep them to a word or two. */
  label: string;
  title?: string;
};

/**
 * A row of mutually exclusive choices over a small, bounded set.
 *
 * Built for config whose type IS a closed union — `width: narrow|normal|wide|full`,
 * `cols: 1|2|3|4`, `padY`, `align`. A `Select` hides those behind a click and
 * gives no sense of a spectrum; showing all four side by side makes the range
 * legible and turns picking one into a single click. It also can't express a
 * value outside the union, which is what keeps stored tokens clampable.
 *
 * Deliberately dependency-free: this is a radio group in a trench coat, and it
 * renders as one for assistive tech.
 */
export function SegmentedControl<T extends string | number>({
  value,
  onChange,
  options,
  size = 'md',
  className,
  disabled,
  ariaLabel,
}: {
  value: T | undefined;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  size?: 'sm' | 'md';
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('inline-flex flex-wrap items-center gap-0.5 rounded-md bg-muted p-0.5', className)}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={active}
            title={o.title ?? o.label}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              'rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
              active
                ? 'bg-background font-medium text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
